import { DocumentType } from '@/types/schema';

/**
 * The curated, ordered list of document types shown as chips in the "Recommended
 * documents" panel of the SmartReview onboarding document-upload step.
 *
 * This is intentionally NOT `DOCUMENT_TYPE_OPTIONS` (which is alphabetized and
 * comprehensive): these are a specifically-ordered, human-friendly subset. Each
 * label can map to more than one `DocumentType` — a chip counts as satisfied
 * when a document of ANY of its types has been uploaded (e.g. any business
 * agreement flavor satisfies "Business agreements").
 */
export interface RecommendedDocumentType {
  label: string;
  documentTypes: DocumentType[];
}

export const RECOMMENDED_DOCUMENT_TYPES: RecommendedDocumentType[] = [
  {
    label: 'Trust documents',
    documentTypes: [DocumentType.SignedTrustDocument],
  },
  { label: 'Wills', documentTypes: [DocumentType.Will] },
  { label: 'Power of attorney', documentTypes: [DocumentType.PowerOfAttorney] },
  {
    label: 'Health care proxies',
    documentTypes: [DocumentType.HealthcareProxy],
  },
  { label: 'Living wills', documentTypes: [DocumentType.LivingWill] },
  {
    label: 'Business agreements',
    documentTypes: [
      DocumentType.PartnershipAgreement,
      DocumentType.OperatingAgreement,
      DocumentType.LlcAgreement,
      DocumentType.ShareholdersAgreement,
    ],
  },
  { label: 'Balance sheets', documentTypes: [DocumentType.BalanceSheet] },
  {
    label: 'Insurance policies',
    documentTypes: [DocumentType.InsurancePolicy],
  },
  {
    label: 'Prenuptial agreements',
    documentTypes: [DocumentType.PrenuptialAgreement],
  },
  { label: 'Letter of wishes', documentTypes: [DocumentType.LetterOfWishes] },
  {
    label: 'Amendments and codicils',
    documentTypes: [
      DocumentType.TrustAmendment,
      DocumentType.TrustRelatedDocument,
      DocumentType.WillCodicil,
    ],
  },
];

/** Copy shown in the document-upload step. */
export const DOCUMENT_UPLOAD_COPY = {
  title: 'Upload documents to get the most out of Luminary',
  subtitle:
    "Luminary works best with full context. Upload your client's existing estate planning documents to power accurate analysis, visualization, and modeling.",
  recommendationsToggle: 'Show recommended documents',
  noDocuments: 'Create waterfall without documents',
  createBalanceSheet: 'Create balance sheet from integration',
  recommendationsHelper:
    'We recommend uploading all core planning documents for each client. All common file types are accepted.',
  uploadWarning:
    'You’ve uploaded more than 25 documents. Prioritize core planning documents for the best results.',
} as const;
